import { Form, Dropdown } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { resolvePath } from '../utils';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import PlaceholderInput from './PlaceholderInput';
import { useFieldContext } from './FieldContext';

/**
 * @param {Object} props
 * @param {boolean} props.required
 * @param {string} props.label
 * @param {boolean} props.hideLabel
 * @param {string} props.id
 * @param {string} props.absoluteId
 * @param {string} props.invalid
 * @param {string} props.className
 * @param {string} props.placeholder
 * @param {number} props.flex
 * @param {number} props.minWidth
 * @param {string[]} props.options
 * @param {boolean} props.preselected
 * @param {boolean} props.disabled
 * @param {boolean} props.frozen
 */
export default function MultiSelectInput({ required = false, label, hideLabel, id, absoluteId, invalid, className = "", placeholder = "Select options...", flex = 1, minWidth = 8, options, disabled, frozen, children, ...rest }) {
    const context = useFieldContext();
    id = absoluteId ?? (context.idPrefix + id);

    const { register, unregister, setValue, getValues, formState, custom: { noAnimation, noAsterisk } } = useFormContext();
    const { errors, isLoading, isSubmitted, disabled: formDisabled } = formState;
    const [selected, setSelected] = useState(getValues(id) ?? []);
    const text = useMemo(getText, [selected]);
    const error = resolvePath(errors, id);
    disabled ||= context.disabled || formDisabled;
    register(id, { ...rest, required, disabled });

    useEffect(() => () => {
        unregister(id, { keepDefaultValue: true });
    }, []);

    useEffect(() => {
        if (!isLoading) {
            setSelected(getValues(id) ?? []);
        }
    }, [formState]);

    useEffect(() => {
        if (getValues(id)?.some?.(v => !options.includes(v))) {
            const newSelected = selected.filter(o => options.includes(o));
            setSelected(newSelected);
            setValue(id, newSelected);
        }
    }, [options]);

    function onOptionChange(checked, option) {
        const newSelected = checked ? [...selected, option] : selected.filter(o => o !== option);
        newSelected.sort((a, b) => options.indexOf(a) - options.indexOf(b));
        setSelected(newSelected);
        setValue(id, newSelected, { shouldValidate: isSubmitted });
    }

    function getText() {
        if (selected.length === 0) {
            return placeholder;
        } else if (selected.length === 1) {
            return selected[0];
        } else if (selected.length === 2) {
            return `${selected[0]} & ${selected[1]}`;
        } else if (selected.length === options.length) {
            return "All";
        } else {
            return `${selected[0]} & ${selected.length - 1} more...`;
        }
    }

    if (isLoading && !noAnimation) {
        return <PlaceholderInput {...{ required, label, hideLabel, flex, minWidth }}>{children}</PlaceholderInput>;
    }

    return (
        <Form.Group className={`form-group flex flex-col w-full ${className}`} style={{ flex, minWidth: minWidth + "rem" }}>
            {!hideLabel && <Form.Label className="text-nowrap text-sm font-medium text-neutral-600 mb-2">{label}{required && !noAsterisk && "*"}</Form.Label>}
            <Dropdown>
                <Dropdown.Toggle disabled={disabled} frozen={frozen ? "" : undefined} isInvalid={error} as={CustomDropdown}>
                    <option>{text}</option>
                </Dropdown.Toggle>
                <Dropdown.Menu as={CustomDropdownMenu}>
                    {options.map(option => (
                        <Dropdown.Item
                            key={option}
                            onClick={e => (e.stopPropagation(), onOptionChange(!selected.includes(option), option))}
                            className="rounded px-2 d-flex gap-2"
                            style={{ cursor: "default" }}>
                            <Form.Check
                                checked={selected.includes(option)}
                                onMouseDown={e => e.preventDefault()}
                                onClick={e => e.stopPropagation()}
                                onChange={e => onOptionChange(e.target.checked, option)}
                            />
                            {option}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            {error && <Form.Control.Feedback type="invalid" className="block mt-0.5 text-sm">{invalid ?? `No ${label.toLowerCase()} selected.`}</Form.Control.Feedback>}
            {children}
        </Form.Group>
    );
}

const CustomDropdown = forwardRef(({ onClick, children, isInvalid, ...rest }, ref) => {
    return (
        <Form.Select {...rest} className={`py-2 text-lg rounded-sm border-neutral-300 border-b-neutral-200 ${isInvalid ? "[&]:border-red-400" : ""} inset-shadow-sm inset-shadow-neutral-200`} ref={ref} isInvalid={isInvalid} size="lg" onMouseDown={e => (e.preventDefault(), e.target.focus())} onClick={onClick}>
            {children}
        </Form.Select>
    );
});

// Destructure show and close to prevent React warning
const CustomDropdownMenu = forwardRef(({ className, style, children, show, close, ...rest }, ref) => {
    return (
        <div {...rest} className={className + " fs-5 p-1"} style={{ ...style, boxShadow: "0 0 5px 1px #00000025", border: "1px solid #999" }} ref={ref}>
            {children}
        </div>
    );
});